import React from 'react';

import type { NavData } from '../types';

interface NavigationContextProviderProps {
  data: NavData;
  children?: React.ReactNode;
}

type NavigationContextData = ReturnType<typeof useNavigationContextData>;

const NavigationContext = React.createContext<NavigationContextData>(
  null as any,
);

const useNavigationContextData = (data: NavData) => {
  const [mainMenuActive, setMainMenuActive] = React.useState(false);
  const [selectedMenuIndex, setSelectedMenuIndex] = React.useState<
    number | null
  >(null);

  const closeSubMenu = React.useCallback(() => {
    setSelectedMenuIndex(null);
  }, []);
  const openMenu = React.useCallback(() => {
    setMainMenuActive(true);
  }, []);
  const closeMenu = React.useCallback(() => {
    setMainMenuActive(false);
    setSelectedMenuIndex(null);
  }, []);
  const onItemSelected = React.useCallback((index: number) => {
    setSelectedMenuIndex((currentIndex) => {
      if (currentIndex === index) {
        setMainMenuActive(false);
        return null;
      } else {
        return index;
      }
    });
  }, []);

  const selectedItem =
    typeof selectedMenuIndex === 'number'
      ? data.menu.main[selectedMenuIndex]
      : null;

  return React.useMemo(
    () => ({
      data,
      main: {
        active: !!selectedItem || mainMenuActive,
        open: openMenu,
        close: closeMenu,
        selectedIndex: selectedMenuIndex,
        selectedItem,
        selectItem: onItemSelected,
      },
      secondary: {
        active: !!selectedItem,
        close: closeSubMenu,
        sections: selectedItem?.subnavigation,
        features: selectedItem?.features,
        template: selectedItem?.template,
      },
    }),
    [
      data,
      mainMenuActive,
      selectedMenuIndex,
      onItemSelected,
      openMenu,
      closeMenu,
      closeSubMenu,
      selectedItem,
    ],
  );
};

export const NavigationContextProvider: React.FC<
  NavigationContextProviderProps
> = ({ children, data }) => {
  const contextData = useNavigationContextData(data);

  return (
    <NavigationContext.Provider value={contextData}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  return React.useContext(NavigationContext);
};
